import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Heart, Brain, CheckCircle, Calendar, ArrowRight, AlertCircle } from 'lucide-react';
import CTA from '../components/CTA';

const MriAnxietyHypnotherapy: React.FC = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How quickly can hypnotherapy help MRI anxiety?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Many clients feel significantly more prepared after just 1-2 sessions. For an upcoming scan, contact Sharon as soon as possible after your appointment is confirmed."
        }
      },
      {
        "@type": "Question",
        "name": "What if I've already panicked and been unable to complete a scan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "This is the most common reason people seek help. A previous panic doesn't predict the next attempt — it's a learned response that can change quickly."
        }
      },
      {
        "@type": "Question",
        "name": "Can hypnotherapy help claustrophobia in other situations too?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. The techniques used for MRI claustrophobia apply to lifts, planes, and any enclosed space."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to understand hypnotherapy to benefit?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. You just need to be willing to relax and follow along. Most people are surprised by how straightforward and effective it is."
        }
      }
    ]
  };

  const faqs = [
    {
      question: "How quickly can hypnotherapy help MRI anxiety?",
      answer: "Many clients feel significantly more prepared after just 1-2 sessions. For an upcoming scan, contact Sharon as soon as possible after your appointment is confirmed."
    },
    {
      question: "What if I've already panicked and been unable to complete a scan?",
      answer: "This is the most common reason people seek help. A previous panic doesn't predict the next attempt — it's a learned response that can change quickly."
    },
    {
      question: "Can hypnotherapy help claustrophobia in other situations too?",
      answer: "Yes. The techniques used for MRI claustrophobia apply to lifts, planes, and any enclosed space."
    },
    {
      question: "Do I need to understand hypnotherapy to benefit?",
      answer: "No. You just need to be willing to relax and follow along. Most people are surprised by how straightforward and effective it is."
    }
  ];

  const symptoms = [
    "Panic at the thought of entering the MRI scanner",
    "Fear of being enclosed in a small, confined space",
    "Worry about having to stay completely still",
    "Dread of the loud, repetitive noise inside the scanner",
    "A history of panicking during previous scans — or avoiding them altogether"
  ];

  const benefits = [
    {
      icon: <Calendar className="w-6 h-6 text-accent-600" />,
      title: "Targeted pre-scan preparation for your specific appointment"
    },
    {
      icon: <Shield className="w-6 h-6 text-accent-600" />,
      title: "Calming techniques you can use inside the scanner"
    },
    {
      icon: <Brain className="w-6 h-6 text-accent-600" />,
      title: "Build confidence to complete the procedure"
    },
    {
      icon: <Heart className="w-6 h-6 text-accent-600" />,
      title: "Address underlying claustrophobia for lasting benefit"
    }
  ];

  return (
    <main>
      <Helmet>
        <title>MRI Anxiety Hypnotherapy Belfast | Claustrophobia MRI | Sharon Clare NI</title>
        <meta name="description" content="Hypnotherapy for MRI anxiety and claustrophobia in Belfast and Northern Ireland. Get through your scan comfortably with targeted hypnotherapy before your appointment." />
        <meta name="keywords" content="MRI anxiety hypnotherapy Belfast, claustrophobia MRI hypnotherapy, fear of MRI scanner hypnotherapy, MRI phobia Northern Ireland" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/mri-anxiety-hypnotherapy/" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
            MRI Anxiety & Claustrophobia Hypnotherapy in Northern Ireland
          </h1>
          <p className="text-xl font-heading text-gray-700 mb-8">
            Get through your scan comfortably. Targeted hypnotherapy preparation — often just 1-2 sessions — so you can complete your MRI with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/book"
              className="btn-primary inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Request a 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" />
              Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Sound Familiar? */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            Sound Familiar?
          </h2>
          <ul className="space-y-3 mb-8">
            {symptoms.map((symptom) => (
              <li key={symptom} className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{symptom}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 leading-relaxed">
            MRI anxiety and claustrophobia are extremely common. The scanner environment — enclosed, noisy, and requiring stillness — triggers the brain's threat response in many people. If you have panicked before, your brain has learned to associate the scanner with danger. The good news: learned responses can be unlearned, often in just one or two sessions.
          </p>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-4">
            How Hypnotherapy Helps MRI Anxiety
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Clinical hypnotherapy works directly with the subconscious response that triggers panic in the scanner. By retraining the brain's learned threat association, you can approach your scan with calm and control. Many people only need 1-2 sessions before their scan date.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map(({ icon, title }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">{icon}</div>
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">
            Common Questions About MRI Anxiety Hypnotherapy
          </h2>
          <div className="space-y-6">
            {faqs.map(({ question, answer }) => (
              <div key={question} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-2">{question}</h3>
                <p className="text-gray-700 leading-relaxed">{answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar link */}
      <section className="py-8 bg-white">
        <div className="container-custom max-w-4xl text-center">
          <a href="/medical-anxiety-hypnotherapy" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center gap-1">
            Learn more about medical procedure anxiety <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Scan Coming Up? Let's Get You Ready"
        subtitle="Book your session now — even a single appointment before your scan can make a significant difference."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default MriAnxietyHypnotherapy;
